import React, { useMemo, useState, Fragment } from 'react';
import { RowData, Inputs, PackageType, BuildPayType, PACKAGE_PRESETS, SALES_SCHOOL_MONTHLY_CAP } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  halfSales: boolean;
  setHalfSales: (val: boolean) => void;
  doubleAttrition: boolean;
  setDoubleAttrition: (val: boolean) => void;
  onTarget: boolean;
  onToggleOnTarget: () => void;
  plusFive: boolean;
  onPlusFive: () => void;
  timeframe?: 3 | 6 | 12;
  setTimeframe?: (t: 3 | 6 | 12) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  setHalfSales,
  doubleAttrition,
  setDoubleAttrition,
  onTarget,
  onToggleOnTarget,
  plusFive,
  onPlusFive,
  timeframe: timeframeProp,
  setTimeframe: setTimeframeProp
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for Monthly Fees breakdown
  const [feePopup, setFeePopup] = useState<{
    rowIdx: number;
    platformFee: number;
    campaignFee: number;
    recruiterFee: number;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for Sales School breakdown
  const [bonusPopup, setBonusPopup] = useState<{
    rowIdx: number;
    placementBonus: number;
    retentionBonus: number;
    x: number;
    y: number;
  } | null>(null);
  // Track which ad spend cell is being edited
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  // Track which campaign cell is being edited
  const [editingCampaign, setEditingCampaign] = useState<number | null>(null);
  const [editCampaignValue, setEditCampaignValue] = useState<string>('');
  // Track which fee cells are being edited
  const [editingBuildFee, setEditingBuildFee] = useState<number | null>(null);
  const [editBuildFeeValue, setEditBuildFeeValue] = useState<string>('');
  const [editingPlatformFee, setEditingPlatformFee] = useState<number | null>(null);
  const [editPlatformFeeValue, setEditPlatformFeeValue] = useState<string>('');
  const [editingCampaignFee, setEditingCampaignFee] = useState<number | null>(null);
  const [editCampaignFeeValue, setEditCampaignFeeValue] = useState<string>('');
  // Track which metric row label is being hovered
  const [hoveredMetricKey, setHoveredMetricKey] = useState<string | null>(null);
  // Track which data cell is being hovered (for crosshair highlight)
  const [hoveredCell, setHoveredCell] = useState<{
    metricKey: string;
    colIdx: number;
  } | null>(null);
  // Timeframe state (controlled if props provided)
  const [timeframeInternal, setTimeframeInternal] = useState<3 | 6 | 12>(6);
  const timeframe = timeframeProp ?? timeframeInternal;
  const setTimeframe = setTimeframeProp ?? setTimeframeInternal;
  // Revenue shift state
  const [revenueShift, setRevenueShift] = useState<number>(0);
  // Sales delay state (number of months to zero out at the start)
  const [salesDelay, setSalesDelay] = useState<number>(0);
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    recruiting: true,
    activeReps: true,
    investment: false,
    revenue: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  // Handle monthly campaign overrides
  const handleCampaignChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    newMonthlyCampaigns[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns
    });
  };
  const handlePushRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns
    });
  };
  const handlePushLeft = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx <= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns
    });
  };
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyAdSpend[arrayIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = monthIndex - 1;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const valueToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(currentValue.toString());
  };
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue) || 0;
      handleAdSpendChange(editingAdSpend, value);
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  const startEditingCampaign = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingCampaign(monthIndex);
    setEditCampaignValue(currentValue.toString());
  };
  const saveCampaignEdit = () => {
    if (editingCampaign !== null) {
      const value = parseFloat(editCampaignValue) || 0;
      handleCampaignChange(editingCampaign, value);
      setEditingCampaign(null);
      setEditCampaignValue('');
    }
  };
  const cancelCampaignEdit = () => {
    setEditingCampaign(null);
    setEditCampaignValue('');
  };
  const handleCampaignKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignEdit();
    }
  };
  // Handle build fee editing - saves to inputs for persistence
  const startEditingBuildFee = (monthIndex: number, currentValue: number) => {
    setEditingBuildFee(monthIndex);
    setEditBuildFeeValue(currentValue.toString());
  };
  const handleBuildFeeChange = (monthIndex: number, value: number) => {
    // Initialize monthly build fees array if not present
    // Index 0 = Build, Index 1 = Month 1, etc. (13 total slots)
    const currentBuildFees = inputs.monthlyBuildFees || Array(13).fill(undefined);
    const newBuildFees = [...currentBuildFees];
    newBuildFees[monthIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyBuildFees: newBuildFees
    });
  };
  const saveBuildFeeEdit = () => {
    if (editingBuildFee !== null) {
      const value = parseFloat(editBuildFeeValue) || 0;
      handleBuildFeeChange(editingBuildFee, value);
      setEditingBuildFee(null);
      setEditBuildFeeValue('');
    }
  };
  const cancelBuildFeeEdit = () => {
    setEditingBuildFee(null);
    setEditBuildFeeValue('');
  };
  const handleBuildFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveBuildFeeEdit();
    } else if (e.key === 'Escape') {
      cancelBuildFeeEdit();
    }
  };
  const startEditingPlatformFee = (monthIndex: number, currentValue: number) => {
    setEditingPlatformFee(monthIndex);
    setEditPlatformFeeValue(currentValue.toString());
  };
  const savePlatformFeeEdit = () => {
    if (editingPlatformFee !== null) {
      setEditingPlatformFee(null);
      setEditPlatformFeeValue('');
    }
  };
  const cancelPlatformFeeEdit = () => {
    setEditingPlatformFee(null);
    setEditPlatformFeeValue('');
  };
  const handlePlatformFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      savePlatformFeeEdit();
    } else if (e.key === 'Escape') {
      cancelPlatformFeeEdit();
    }
  };
  const startEditingCampaignFee = (monthIndex: number, currentValue: number) => {
    setEditingCampaignFee(monthIndex);
    setEditCampaignFeeValue(currentValue.toString());
  };
  const saveCampaignFeeEdit = () => {
    if (editingCampaignFee !== null) {
      setEditingCampaignFee(null);
      setEditCampaignFeeValue('');
    }
  };
  const cancelCampaignFeeEdit = () => {
    setEditingCampaignFee(null);
    setEditCampaignFeeValue('');
  };
  const handleCampaignFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignFeeEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignFeeEdit();
    }
  };
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition && revenueShift === 0 && salesDelay === 0) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply sales delay (zero out sales for first N months)
      // Also shift residual revenue when sales are delayed
      if (salesDelay > 0 && idx > 0 && idx <= salesDelay) {
        // Zero out sales and residual for months 1 through salesDelay
        newRow.totalSales = 0;
        newRow.grossRevenue = 0;
        newRow.residualRevenue = 0;
      } else if (salesDelay > 0 && idx > salesDelay) {
        // For months after the delay, shift the residual revenue
        // The residual revenue should come from (idx - salesDelay) position in original rows
        const sourceIdx = idx - salesDelay;
        if (sourceIdx > 0 && sourceIdx < rows.length) {
          newRow.residualRevenue = rows[sourceIdx].residualRevenue || 0;
        } else {
          newRow.residualRevenue = 0;
        }
      }
      // Apply revenue shift (this can further modify grossRevenue and residualRevenue)
      if (revenueShift !== 0) {
        const sourceIdx = idx - revenueShift;
        if (idx > 0 && sourceIdx >= 0 && sourceIdx < rows.length) {
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = rows[sourceIdx].totalSales;
          }
          newRow.grossRevenue = rows[sourceIdx].grossRevenue;
          newRow.residualRevenue = rows[sourceIdx].residualRevenue || 0;
        } else if (idx > 0) {
          if (salesDelay === 0 || idx > salesDelay) {
            newRow.totalSales = 0;
          }
          newRow.grossRevenue = 0;
          newRow.residualRevenue = 0;
        }
      }
      // Apply half sales scenario
      if (halfSales) {
        newRow.totalSales = newRow.totalSales * 0.5;
        newRow.grossRevenue = newRow.grossRevenue * 0.5;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        newRow.totalSales = newRow.totalSales * retentionMultiplier;
        newRow.grossRevenue = newRow.grossRevenue * retentionMultiplier;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * retentionMultiplier;
      }
      newRow.grossProfit = newRow.grossRevenue * (inputs.gpm / 100);
      newRow.netProfit = newRow.grossProfit - row.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition, revenueShift, salesDelay, inputs.gpm, inputs.ticket]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  const displayRows = useMemo(() => {
    return finalRows.slice(0, timeframe + 1);
  }, [finalRows, timeframe]);
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex;
    switch (metricKey) {
      case 'activeCampaigns':
        return `Number of active campaigns for this month.\nAdjusting this will recalculate ad spend and hires.`;
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ CPH = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 0) return 'Build period - no active reps yet';
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        return `Formula: Previous month's launched reps × Day 30 retention\n\nActual: Single cohort at 30-60 days = ${row.active30d.toFixed(1)} reps`;
      case 'active60d':
        if (monthNum <= 2) return monthNum === 0 ? 'Build period' : 'Not enough time elapsed (need 60 days)';
        return `Formula: 2 months ago launched reps × Day 60 retention\n\nActual: Single cohort at 60-90 days = ${row.active60d.toFixed(1)} reps`;
      case 'active90d':
        if (monthNum < 4) return monthNum === 0 ? 'Build period' : 'Building cohort (need 90 days)...';
        if (monthNum === 4) {
          return `Formula: First cohort reaching 90 days\n\nActual: ${row.active90d.toFixed(1)} reps (90-120 days old)`;
        }
        return `Formula: Cumulative count of all reps 90+ days old\n\nActual: ${row.active90d.toFixed(1)} (90-120d cohort) + ${row.veterans.toFixed(1)} (120d+ veterans)\n= ${(row.active90d + row.veterans).toFixed(1)} total`;
      case 'veterans':
        if (monthNum <= 4) return 'No veterans yet (need 120+ days)';
        return `Formula: Previous month's 90d+ reps with attrition + new 120d graduates\n\nActual: ${row.veterans.toFixed(1)} veterans (120+ days old)`;
      case 'teamSize':
        const ts_launched = row.launched * 0.5;
        const ts_30to60 = row.active30d;
        const ts_60to90 = row.active60d;
        const ts_90plus = rowIndex === 4 ? row.active90d : row.active90d + row.veterans;
        return `Formula: Launched (0-30d at 50%) + Active 30d+ + Active 60d+ + Active 90d+\n\nBreakdown:\n• Launched (0-30d): ${ts_launched.toFixed(1)} (50% weight)\n• Active 30d+ (30-60d): ${ts_30to60.toFixed(1)}\n• Active 60d+ (60-90d): ${ts_60to90.toFixed(1)}\n• Active 90d+ (90d+): ${ts_90plus.toFixed(1)}\n\nTotal: ${(ts_launched + ts_30to60 + ts_60to90 + ts_90plus).toFixed(1)}`;
      case 'adSpend':
        const scenarioNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario active but ad spend unchanged' : '';
        return `Formula: Monthly ad spend × campaigns\n\nActual: ${fmtMoney(row.adSpend)} total ad spend${scenarioNote}`;
      case 'buildFee':
        if (row.buildFee === 0) return 'No build fee this month';
        return `Formula: Build & Implementation Fee\n\nActual: ${fmtMoney(row.buildFee)} (${monthNum === 0 ? 'Build' : `Month ${monthNum} payment`})\n\nClick to edit any cell in this row.`;
      case 'platformFee':
        return `Formula: HIRE platform monthly fee\n\nActual: ${fmtMoney(row.platformFee)} (system access & support)`;
      case 'campaignFee':
        return `Formula: HIRE campaign management fee × campaigns\n\nActual: ${fmtMoney(row.campaignFee)} (campaign management)`;
      case 'monthlyFees':
        {
          const hasCorporateRecruiter = row.recruiterFee > 0;
          const recruiterBreakdown = hasCorporateRecruiter ? `\nCorporate Recruiter: ${fmtMoney(row.recruiterFee)}` : '';
          const totalMonthlyFees = row.platformFee + row.campaignFee + row.recruiterFee;
          const formula = hasCorporateRecruiter ? 'Platform Fee + Campaign Management + Corporate Recruiter' : 'Platform Fee + Campaign Management';
          return `Formula: ${formula}\n\nPlatform: ${fmtMoney(row.platformFee)}\nCampaign Management: ${fmtMoney(row.campaignFee)}${recruiterBreakdown}\nTotal: ${fmtMoney(totalMonthlyFees)}`;
        }
      case 'performanceBonuses':
        {
          const salesSchoolRate = PACKAGE_PRESETS[inputs.pkg].bonusLaunch;
          const uncappedSalesSchool = Math.floor(row.launched) * salesSchoolRate;
          const salesSchool = !inputs.pkg || inputs.salesSchoolEnabled === false ? 0 : Math.min(SALES_SCHOOL_MONTHLY_CAP, uncappedSalesSchool);
          const capNote = uncappedSalesSchool > SALES_SCHOOL_MONTHLY_CAP ? `\nCapped from ${fmtMoney(uncappedSalesSchool)}` : '';
          return `Formula: Current month's launched reps (rounded down) × ${fmtMoney(salesSchoolRate)}, capped at ${fmtMoney(SALES_SCHOOL_MONTHLY_CAP)}\n\nSales School: ${fmtMoney(salesSchool)}${capNote}`;
        }
      case 'placementBonus':
        {
          const salesSchoolRate = PACKAGE_PRESETS[inputs.pkg].bonusLaunch;
          const uncappedSalesSchool = Math.floor(row.launched) * salesSchoolRate;
          const salesSchool = !inputs.pkg || inputs.salesSchoolEnabled === false ? 0 : Math.min(SALES_SCHOOL_MONTHLY_CAP, uncappedSalesSchool);
          const capNote = uncappedSalesSchool > SALES_SCHOOL_MONTHLY_CAP ? `\nCapped from ${fmtMoney(uncappedSalesSchool)}` : '';
          return `Formula: Current month's launched reps (rounded down) × ${fmtMoney(salesSchoolRate)}, capped at ${fmtMoney(SALES_SCHOOL_MONTHLY_CAP)}\n\nSales School: ${fmtMoney(salesSchool)}${capNote}`;
        }
      case 'retentionBonus':
        const pkgType = inputs.pkg;
        if (pkgType === 'edge') {
          return 'HIRE Edge package does not include retention bonuses';
        }
        if (monthNum === 0) {
          return 'Build period - no bonuses billed yet';
        }
        const prevMonthForRetention = finalRows[monthNum - 1];
        let bonus30d = 0;
        let bonus60d = 0;
        let bonus90d = 0;
        let explanation = "Formula: Previous month's active reps × $250\n";
        explanation += '(Billed month after hitting milestone)\n';
        explanation += 'Rounded down to nearest $250\n\n';
        if (pkgType === 'advantage' || pkgType === 'summit') {
          const raw30d = prevMonthForRetention.active30d * 250;
          bonus30d = Math.floor(raw30d / 250) * 250;
          explanation += `30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} active × $250 = ${fmtMoney(bonus30d)}\n`;
          const raw60d = prevMonthForRetention.active60d * 250;
          bonus60d = Math.floor(raw60d / 250) * 250;
          explanation += `60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} active × $250 = ${fmtMoney(bonus60d)}\n`;
          const raw90d = prevMonthForRetention.active90d * 250;
          bonus90d = Math.floor(raw90d / 250) * 250;
          explanation += `90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} active × $250 = ${fmtMoney(bonus90d)}\n`;
        }
        const totalBonus = bonus30d + bonus60d + bonus90d;
        explanation += `Total: ${fmtMoney(totalBonus)}`;
        let retentionBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        retentionBreakdown += `Billing for milestones hit in ${prevMonthForRetention.period}:\n`;
        if (prevMonthForRetention.active30d > 0) {
          const cohortMonth = monthNum - 2;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active60d > 0) {
          const cohortMonth = monthNum - 3;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active90d > 0) {
          const cohortMonth = monthNum - 4;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        const retentionAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return explanation + retentionBreakdown + retentionAttritionNote;
      case 'bonuses':
        return `Formula: Launch + Day 30 + Day 60 + Day 90 bonuses\n\nActual: ${fmtMoney(row.bonuses)} total bonuses this month`;
      case 'totalCost':
        return `Formula: Sum of all fees, bonuses, and ad spend (rounded)\n\nActual: ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario: Sales reduced by 50%' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return `Formula: Sales from reps in first 90 days + Veteran sales\n\nActual: Reps 0-90 days making ${inputs.salesFirst90Total} total sales over 90 days + ${row.veterans.toFixed(1)} veterans × ${inputs.salesVetMonthly} sales/month\n= ${row.totalSales.toFixed(1)} total sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Formula: Pullthrough / Realization Rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales are realized`;
      case 'grossRevenue':
        const revenueNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied to sales' : '';
        const residualNote = row.residualRevenue && row.residualRevenue > 0 ? `\n\nResidual Revenue: ${fmtMoney(row.residualRevenue)}\nInitial Sales Revenue: ${fmtMoney(row.grossRevenue - row.residualRevenue)}` : '';
        return `Formula: Total Sales × Realization Rate × Avg Ticket${residualNote}\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}${revenueNote}`;
      case 'grossProfit':
        const profitNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied' : '';
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}${profitNote}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Formula: Sum of all net profits from Build to ${row.period}\n\nActual: Running total = ${fmtMoney(row.cumProfit)}`;
      case 'roi':
        let cumGrossProfit = 0;
        let cumTotalCost = 0;
        for (let i = 0; i <= rowIndex; i++) {
          cumGrossProfit += finalRows[i].grossProfit;
          cumTotalCost += finalRows[i].totalCost;
        }
        const roiPercent = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
        return `Formula: (Gross Profit To Date ÷ Total Investment To Date) × 100\n\nActual: ${fmtMoney(cumGrossProfit)} ÷ ${fmtMoney(cumTotalCost)} × 100\n= ${roiPercent.toFixed(1)}%`;
      case 'higherPartnership':
        return `Subtotal of Build, Platform, Campaign, and Bonus fees`;
      default:
        return 'Calculation details';
    }
  };
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-slate-600',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'activeCampaigns',
      label: 'Active Campaigns',
      format: (v: number) => {
        return v % 1 === 0 ? v.toString() : v.toFixed(1);
      },
      editable: true,
      clickToEdit: true,
      centered: true
    }, {
      key: 'adSpendPerCampaign',
      label: 'Ad Spend per Campaign',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true,
      centered: true
    }, {
      key: 'avgCPH',
      label: 'Average CPH',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString(),
      bold: true
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-slate-700',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Active Reps (Average)',
      format: (v: number) => v.toFixed(1),
      bold: true,
      calculated: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Monthly Investment',
    color: 'bg-slate-500',
    collapsible: true,
    showHeaderData: false,
    metrics: [...(inputs.pkg ? [{
      key: 'buildFee' as const,
      label: 'Build & Implementation',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }, {
      key: 'monthlyFees' as const,
      label: 'Monthly Fees',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'performanceBonuses' as const,
      label: <button type="button" onClick={() => onInputsChange({
        ...inputs,
        salesSchoolEnabled: inputs.salesSchoolEnabled === false
      })} className="font-inherit underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 rounded" title={inputs.salesSchoolEnabled === false ? 'Restore all Sales School charges' : 'Clear all Sales School charges'} aria-label={inputs.salesSchoolEnabled === false ? 'Restore all Sales School charges' : 'Clear all Sales School charges'} data-id="element-390" data-mp="nej2v4pav0xv">
                    Sales School
                  </button>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'higherPartnership' as const,
      label: 'HIRE Partnership',
      format: (v: number) => fmtMoney(v),
      bold: true
    }] : []), {
      key: 'adSpend',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-slate-800',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'totalSales',
      label: <div className="flex items-center justify-between w-full group" data-id="element-391" data-mp="631b71e3k2pj">
              <span data-id="element-392" data-mp="22hefyxfr13x">Total Sales</span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200" data-id="element-393" data-mp="23injxuw5dj0">
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.max(0, prev - 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Reduce sales delay (enable sales in earlier months)" data-id="element-394" data-mp="3o9p9o8en5rm8">
                  ◀
                </button>
                {salesDelay > 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-395" data-mp="2ei6euf4d8kbl">
                    {salesDelay}mo
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.min(11, prev + 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Delay sales (zero out sales in early months)" data-id="element-396" data-mp="2o6y2wc6b2kh8">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Realization Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, ...(inputs.revenueCadence !== 'one-time' ? [{
      key: 'residualRevenue' as const,
      label: inputs.revenueCadence === 'monthly' ? 'Monthly Residual Revenue' : 'Quarterly Residual Revenue',
      format: (v: number) => fmtMoney(v)
    }] : []), {
      key: 'grossRevenue',
      label: <div className="flex items-center justify-between w-full group" data-id="element-397" data-mp="30pag5mj22jco">
              <span data-id="element-398" data-mp="35odfcqcctvcq">
                {inputs.revenueCadence !== 'one-time' ? 'Total Monthly Revenue' : 'Monthly Gross Revenue'}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200 relative z-20 mr-1" data-id="element-399" data-mp="169brqa2bw3ek">
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev - 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue earlier" data-id="element-400" data-mp="11gimll2y6t1y">
                  ◀
                </button>
                {revenueShift !== 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-401" data-mp="ejyv501xc9t2">
                    {revenueShift > 0 ? '+' : ''}
                    {revenueShift}
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev + 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shift revenue later" data-id="element-402" data-mp="28u2mxxo67vpm">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: 'Monthly Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'netProfit',
      label: 'Monthly Net Profit',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      bold: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      bg: 'bg-gray-100'
    }, {
      key: 'roi',
      label: 'ROI',
      format: (v: number) => `${Math.round(v)}%`,
      highlight: true,
      bg: 'bg-gray-200'
    }]
  }];
  return <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg" data-id="element-403" data-mp="18p21vi2pv6z7">
      <div className="flex items-center justify-between mb-4" data-id="element-404" data-mp="14m5r2djf4f5t">
        <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-405" data-mp="14orvw5excgkc">
          {inputs.companyName ? `Monthly Projections for ${inputs.companyName}` : 'Monthly Projections'}
        </h2>

        <div className="flex items-center gap-3" data-id="element-406" data-mp="1sc01kog9lrmw">
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-407" data-mp="1sdsyvd4zmzi8">
            <button onClick={() => setTimeframe(3)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-408" data-mp="1nezy8lasv2zh">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-409" data-mp="2fzwmi7rxpb5y">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-410" data-mp="2ayz722yb6v85">
              12M
            </button>
          </div>

          {/* Package Dropdown */}
          <select value={inputs.pkg} onChange={e => onInputsChange({
          ...inputs,
          pkg: e.target.value as PackageType
        })} className="px-3 py-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-xs font-semibold" data-id="element-411" data-mp="1n8v3fvl4evh5">
            <option value="" data-id="element-412" data-mp="3l0d25em7hwne">Select</option>
            <option value="edge" data-id="element-413" data-mp="3g7biktb218xh">HIRE Edge</option>
            <option value="advantage" data-id="element-414" data-mp="3g5gvb8cb94q8">HIRE Advantage</option>
            <option value="summit" data-id="element-415" data-mp="3b9ice09r4ex7">HIRE Summit</option>
          </select>

          {/* Scenario Toggles */}
          <button onClick={() => setHalfSales(!halfSales)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${halfSales ? 'bg-red-500 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'}`} data-id="element-416" data-mp="3786j9kro5bk9">
            {halfSales ? '✓ ' : ''}50% Sales Scenario
          </button>
          <button onClick={() => setDoubleAttrition(!doubleAttrition)} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${doubleAttrition ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`} data-id="element-417" data-mp="3pxmvh5cpusgi">
            {doubleAttrition ? '✓ ' : ''}2x Attrition Scenario
          </button>
          <button onClick={onPlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-418" data-mp="yyqndlvj59ch">
            {plusFive ? '✓ ' : ''}+5%
          </button>
          <button onClick={onToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-419" data-mp="1ct12rclmzr5v">
            {onTarget ? '✓ ' : ''}On Target
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border-2 border-gray-200" data-id="element-420" data-mp="3oi0qgz80yar6">
        <table className="w-full border-collapse text-sm" style={{
        tableLayout: 'fixed'
      }} data-id="element-421" data-mp="3siuh40cuq3nr">
          <thead className="sticky top-0 z-20" data-id="element-422" data-mp="39j7r5vt2jibm">
            <tr className="bg-navy text-white" data-id="element-423" data-mp="34lemj77eywdg">
              <th className="bg-navy text-xs font-extrabold uppercase tracking-wide p-2 text-left border-r border-gray-300" style={{
              width: '170px'
            }} data-id="element-424" data-mp="34kmz4jnz04y9">
                <span className="inline-block" data-id="element-425" data-mp="2qlwr48zm4kv4">Metric</span>
              </th>
              {displayRows.map((row, idx) => {
              const displayPeriod = row.period === 'Build' ? 'Build' : row.period.replace('Month', 'Mo');
              const isQuarterEnd = idx > 0 && idx % 3 === 0;
              const isColHighlighted = hoveredCell !== null && hoveredCell.colIdx === idx;
              return <th key={row.period} className={`text-xs font-extrabold uppercase tracking-wide p-2 text-center whitespace-nowrap border-r border-gray-300 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isColHighlighted ? 'bg-navy/80 text-amber' : 'bg-navy'}`} data-id="element-426" data-mp="9ykhmj9quuyo" data-mpi={idx}>
                    {displayPeriod}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="bg-white" data-id="element-427" data-mp="dg8guyx3oth8">
            {metricGroups.map((group, groupIdx) => {
            const isExpanded = group.collapsible ? expandedSections[group.id as keyof typeof expandedSections] : true;
            const showDetails = !group.collapsible || isExpanded;
            return <Fragment key={group.id} data-id="element-428" data-mp="wekr6nzr09ee" data-mpi={groupIdx}>
                  {!group.noHeader && <tr className={`${group.color}`} data-id="element-429" data-mp="1k4f7jfja0g3e">
                      <td className={`${group.color} p-2 font-extrabold text-white text-xs border-r border-gray-300 ${group.collapsible ? 'cursor-pointer hover:bg-opacity-80' : ''}`} onClick={() => group.collapsible && toggleSection(group.id as keyof typeof expandedSections)} data-id="element-430" data-mp="1k2ub1kdein2w">
                        <div className="flex items-center gap-2" data-id="element-431" data-mp="2jlk7zm5rhse8">
                          {group.collapsible && <span className="text-white" data-id="element-432" data-mp="37an05ny0v3mz">
                              {isExpanded ? '▼' : '▶'}
                            </span>}
                          <span className="uppercase tracking-wide text-xs inline-block" data-id="element-433" data-mp="2xj8vl1r7rdmz">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                  const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                  return <td key={row.period} className={`${group.color} p-2 text-center font-extrabold text-white border-r border-gray-300 ${isQuarterEnd ? 'border-r-4' : ''}`} data-id="element-434" data-mp="88bvagvt6aad" data-mpi={rowIdx}></td>;
                })}
                    </tr>}

                  {showDetails && group.metrics.map((metric, metricIdx) => {
                const isLastInGroup = metricIdx === group.metrics.length - 1;
                const isRowHighlighted = hoveredMetricKey === metric.key;
                const isCrosshairRow = hoveredCell !== null && hoveredCell.metricKey === metric.key;
                return <tr key={metric.key} className="h-10 bg-white" data-id="element-435" data-mp="1am0zobiefx64" data-mpi={metricIdx}>
                          <td className={`p-2 ${group.noHeader ? '' : 'pl-4'} text-gray-900 ${metric.bold ? 'font-extrabold text-xs' : 'font-bold text-xs'} border-r border-gray-300 transition-colors cursor-pointer whitespace-nowrap relative ${isRowHighlighted ? 'bg-amber-50' : isCrosshairRow ? 'bg-amber-50' : 'bg-white'}`} onMouseEnter={() => {
                    setHoveredMetricKey(metric.key);
                    setHoveredCell(null);
                  }} onMouseLeave={() => setHoveredMetricKey(null)} data-id="element-436" data-mp="15nfya8rev7g3">
                            <span className="inline-block" data-id="element-437" data-mp="yxy7eno1xilb">{metric.label}</span>
                          </td>
                          {displayRows.map((row, rowIdx) => {
                    let value: number;
                    let displayValue: React.ReactNode;
                    const getPlacementBonus = (r: RowData, _idx: number) => {
                      if (!inputs.pkg || inputs.salesSchoolEnabled === false) {
                        return 0;
                      }
                      return Math.min(SALES_SCHOOL_MONTHLY_CAP, Math.floor(r.launched) * PACKAGE_PRESETS[inputs.pkg].bonusLaunch);
                    };
                    const getRetentionBonus = () => 0;
                    const getAdSpendPerCampaign = (idx: number) => {
                      if (idx === 0) return 0;
                      const monthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
                      return monthlyAdSpend[idx - 1];
                    };
                    const getAvgCPH = (idx: number) => {
                      if (idx === 0) return 0;
                      return inputs.cph;
                    };
                    const getBuildFeeValue = (idx: number) => {
                      // No package selected → ignore any stored build fees
                      if (!inputs.pkg) return 0;
                      const customBuildFees = inputs.monthlyBuildFees;
                      if (customBuildFees && customBuildFees[idx] !== undefined && customBuildFees[idx] !== null) {
                        return customBuildFees[idx];
                      }
                      return row.buildFee;
                    };
                    if (metric.key === 'teamSize') {
                      const launched0to30 = row.launched * 0.5;
                      const active30to60 = row.active30d;
                      const active60to90 = row.active60d;
                      const active90plus = row.active90d + row.veterans;
                      value = launched0to30 + active30to60 + active60to90 + active90plus;
                    } else if (metric.key === 'active30d') {
                      value = row.active30d;
                    } else if (metric.key === 'active60d') {
                      value = row.active60d;
                    } else if (metric.key === 'active90d') {
                      if (rowIdx === 4) {
                        value = row.active90d;
                      } else if (rowIdx > 4) {
                        value = row.active90d + row.veterans;
                      } else {
                        value = 0;
                      }
                    } else if (metric.key === 'adSpendPerCampaign') {
                      value = getAdSpendPerCampaign(rowIdx);
                    } else if (metric.key === 'avgCPH') {
                      value = getAvgCPH(rowIdx);
                    } else if (metric.key === 'placementBonus') {
                      value = getPlacementBonus(row, rowIdx);
                    } else if (metric.key === 'retentionBonus') {
                      value = getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'performanceBonuses') {
                      value = getPlacementBonus(row, rowIdx) + getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'buildFee') {
                      value = getBuildFeeValue(rowIdx);
                    } else if (metric.key === 'monthlyFees') {
                      value = row.platformFee + row.campaignFee + row.recruiterFee;
                    } else if (metric.key === 'higherPartnership') {
                      const pBonus = getPlacementBonus(row, rowIdx);
                      const rBonus = getRetentionBonus(row, rowIdx);
                      const buildFeeVal = getBuildFeeValue(rowIdx);
                      value = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + pBonus + rBonus;
                    } else if (metric.key === 'totalCost') {
                      if (!inputs.pkg) {
                        // No package selected: only ad spend contributes
                        value = row.adSpend;
                      } else {
                        const pBonus = getPlacementBonus(row, rowIdx);
                        const rBonus = getRetentionBonus(row, rowIdx);
                        const buildFeeVal = getBuildFeeValue(rowIdx);
                        const subtotal = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + pBonus + rBonus;
                        value = subtotal + row.adSpend;
                      }
                    } else if (metric.key === 'roi') {
                      let cumGrossProfit = 0;
                      let cumTotalCost = 0;
                      for (let i = 0; i <= rowIdx; i++) {
                        cumGrossProfit += finalRows[i].grossProfit;
                        cumTotalCost += finalRows[i].totalCost;
                      }
                      value = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
                    } else if (metric.key === 'netProfit' && !inputs.pkg) {
                      // No package: net profit = gross profit − ad spend only
                      value = row.grossProfit - row.adSpend;
                    } else if (metric.key === 'cumProfit' && !inputs.pkg) {
                      // No package: profit-to-date sums (grossProfit − adSpend) across rows
                      let cum = 0;
                      for (let i = 0; i <= rowIdx; i++) {
                        cum += finalRows[i].grossProfit - finalRows[i].adSpend;
                      }
                      value = cum;
                    } else {
                      value = row[metric.key as keyof RowData] as number;
                    }
                    const formatted = metric.format(value);
                    if (metric.key === 'activeCampaigns' && metric.editable && rowIdx > 0) {
                      if (editingCampaign === rowIdx) {
                        displayValue = <input type="number" min="0" step="0.5" value={editCampaignValue} onChange={e => setEditCampaignValue(e.target.value)} onKeyDown={handleCampaignKeyDown} onBlur={saveCampaignEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-438" data-mp="1oxh5vhcdqaaj" />;
                      } else {
                        displayValue = <div className="relative flex items-center justify-center group/cell" data-id="element-439" data-mp="2yyuzspojj5o7">
                                    {rowIdx > 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushLeft(rowIdx);
                          }} title={`← Copy ${formatted} to all earlier months`} className="absolute left-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-440" data-mp="2vwgnwmevcg8f">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-441" data-mp="3okcinlkvny2k">
                                          <path d="M11 7H3M6 4L3 7l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-442" data-mp="3tk8prtovg5mc" />
                                        </svg>
                                      </button>}
                                    <span onClick={() => startEditingCampaign(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-443" data-mp="3tf5job94sj47">
                                      {formatted}
                                    </span>
                                    {rowIdx < displayRows.length - 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushRight(rowIdx);
                          }} title={`Copy ${formatted} to all months from here →`} className="absolute right-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-444" data-mp="mmgzp835th01">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-445" data-mp="3q378fuq4m85f">
                                          <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-446" data-mp="1jchimqgul39f" />
                                        </svg>
                                      </button>}
                                  </div>;
                      }
                    } else if (metric.key === 'activeCampaigns' && rowIdx === 0) {
                      displayValue = formatted;
                    } else if (metric.key === 'buildFee' && metric.clickToEdit) {
                      if (editingBuildFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editBuildFeeValue} onChange={e => setEditBuildFeeValue(e.target.value)} onKeyDown={handleBuildFeeKeyDown} onBlur={saveBuildFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-447" data-mp="t8zxci6nnsp3" />;
                      } else {
                        displayValue = <span onClick={() => startEditingBuildFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-448" data-mp="3dsodbbz6zq9r">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'platformFee' && metric.clickToEdit) {
                      if (editingPlatformFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editPlatformFeeValue} onChange={e => setEditPlatformFeeValue(e.target.value)} onKeyDown={handlePlatformFeeKeyDown} onBlur={savePlatformFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-449" data-mp="2b6mim0qxtcde" />;
                      } else {
                        displayValue = <span onClick={() => startEditingPlatformFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-450" data-mp="22et1ic5m11ej">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'campaignFee' && metric.clickToEdit) {
                      if (editingCampaignFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editCampaignFeeValue} onChange={e => setEditCampaignFeeValue(e.target.value)} onKeyDown={handleCampaignFeeKeyDown} onBlur={saveCampaignFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-451" data-mp="14jywc00xjzju" />;
                      } else {
                        displayValue = <span onClick={() => startEditingCampaignFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-452" data-mp="1vb2icnqkibtd">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && metric.clickToEdit && rowIdx > 0) {
                      if (editingAdSpend === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-453" data-mp="3tn4b1opsnlqs" />;
                      } else {
                        displayValue = <span onClick={() => startEditingAdSpend(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-454" data-mp="33d3k46n5cqc9">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx === 0) {
                      displayValue = formatted;
                    } else if (metric.key === 'monthlyFees' && value > 0) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setFeePopup({
                          rowIdx,
                          platformFee: row.platformFee,
                          campaignFee: row.campaignFee,
                          recruiterFee: row.recruiterFee,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-455" data-mp="1of92g57p7im1">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'performanceBonuses' && value > 0) {
                      const placement = getPlacementBonus(row, rowIdx);
                      const retention = getRetentionBonus(row, rowIdx);
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setBonusPopup({
                          rowIdx,
                          placementBonus: placement,
                          retentionBonus: retention,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-456" data-mp="305dsd1wwrp7d">
                                  {formatted}
                                </span>;
                    } else {
                      displayValue = formatted;
                    }
                    const isQuarterEnd = rowIdx > 0 && rowIdx % 3 === 0;
                    let cellClass = `p-2 text-center whitespace-nowrap relative text-sm font-semibold border-r border-gray-300 hover:bg-gray-100 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isRowHighlighted ? 'bg-amber-50' : 'bg-white'}`;
                    if (metric.highlight) {
                      if (value >= 0) {
                        cellClass += ' text-green-600 font-extrabold';
                      } else {
                        cellClass += ' text-red-600 font-extrabold';
                      }
                    } else if (metric.bold) {
                      cellClass += ' font-extrabold text-gray-900';
                    } else {
                      cellClass += ' text-gray-800';
                    }
                    return <td key={row.period} className={cellClass} onMouseEnter={() => {
                      setHoveredCell({
                        metricKey: metric.key,
                        colIdx: rowIdx
                      });
                      setHoveredMetricKey(null);
                    }} onMouseLeave={() => setHoveredCell(null)} data-id="element-457" data-mp="1qbfvgeo84111" data-mpi={rowIdx}>
                                {displayValue}
                              </td>;
                  })}
                        </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border-2 border-amber max-w-sm text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-458" data-mp="31s90uvjsa1h7">
          {tooltip.content}
        </div>}

      {feePopup && <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setFeePopup(null)} data-id="element-459" data-mp="2floh33qu0otm" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: feePopup.x,
        top: feePopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-460" data-mp="2ki541umaox54">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-461" data-mp="2y99wocr8wp5y">
              <span className="font-bold text-navy" data-id="element-462" data-mp="33az83tidr4uk">
                Monthly Fees Breakdown
              </span>
              <button onClick={() => setFeePopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-463" data-mp="3877spmfcjq94">
                ×
              </button>
            </div>
            <div className="space-y-1.5" data-id="element-464" data-mp="3r6korsayrkaa">
              <div className="flex items-center justify-between gap-4" data-id="element-465" data-mp="3r7drmi6sxpn5">
                <span className="text-gray-600" data-id="element-466" data-mp="3md9l201g7jml">Platform Fee</span>
                <span className="font-semibold text-gray-900" data-id="element-467" data-mp="3icfwd4g6m0sn">
                  {fmtMoney(feePopup.platformFee)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-468" data-mp="3dcbb714bwhw3">
                <span className="text-gray-600" data-id="element-469" data-mp="dyhflpo4uun9">Campaign Management</span>
                <span className="font-semibold text-gray-900" data-id="element-470" data-mp="9yh0e2t7mff5">
                  {fmtMoney(feePopup.campaignFee)}
                </span>
              </div>
              {feePopup.recruiterFee > 0 && <div className="flex items-center justify-between gap-4" data-id="element-471" data-mp="3w3kollh7zqsy">
                  <span className="text-gray-600" data-id="element-472" data-mp="2pp37uoqgniwp">Corporate Recruiter</span>
                  <span className="font-semibold text-gray-900" data-id="element-473" data-mp="2pm7c972356bf">
                    {fmtMoney(feePopup.recruiterFee)}
                  </span>
                </div>}
              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-gray-200" data-id="element-474" data-mp="1ivq7l76a8sfv">
                <span className="font-bold text-navy" data-id="element-475" data-mp="1dwwrd6zm2nc0">Total</span>
                <span className="font-bold text-navy" data-id="element-476" data-mp="1rrpfgttkhbu7">
                  {fmtMoney(feePopup.platformFee + feePopup.campaignFee + feePopup.recruiterFee)}
                </span>
              </div>
            </div>
          </div>
        </>}

      {bonusPopup && <>
          <div className="fixed inset-0 z-40" onClick={() => setBonusPopup(null)} data-id="element-477" data-mp="2apifnoct37nw" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: bonusPopup.x,
        top: bonusPopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-478" data-mp="26ogeh1zbwlja">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-479" data-mp="25z3nytf71ko">
              <span className="font-bold text-navy" data-id="element-480" data-mp="66j2r7xk0f9g">Sales School Breakdown</span>
              <button onClick={() => setBonusPopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-481" data-mp="3ojz2umk4q6gt">
                ×
              </button>
            </div>
            <div className="flex items-center justify-between gap-4" data-id="element-482" data-mp="3jlp4k333ypqz">
              <span className="text-gray-600" data-id="element-483" data-mp="3jjugifwjihio">Sales School</span>
              <span className="font-bold text-navy" data-id="element-484" data-mp="35n7i08oxvjan">
                {fmtMoney(bonusPopup.placementBonus)}
              </span>
            </div>
          </div>
        </>}
    </div>;
}